import React, { useState, useEffect } from 'react';
import axiosInstance from '../../utils/axiosInstance';
import { Trash2, Edit2, Plus, Save, X, Globe, Type, FileText } from 'lucide-react';

const AnimationTextManage = () => {
    const [texts, setTexts] = useState([]);
    
    // Form State
    const [formData, setFormData] = useState({ text: '', sub_text: '' });
    
    const [editId, setEditId] = useState(null);
    const [status, setStatus] = useState({ type: '', msg: '' });

    const fetchTexts = async () => {
        try {
            const res = await axiosInstance.get('/animation-texts');
            setTexts(res.data);
        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => {
        fetchTexts();
    }, []);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.text.trim()) return;

        try {
            if (editId) {
                await axiosInstance.put(`/animation-texts/${editId}`, formData);
                setStatus({ type: 'success', msg: 'Updated!' });
            } else {
                await axiosInstance.post('/animation-texts', formData);
                setStatus({ type: 'success', msg: 'Added!' });
            }
            setFormData({ text: '', sub_text: '' });
            setEditId(null);
            fetchTexts();
        } catch (err) {
            setStatus({ type: 'error', msg: 'Operation failed.' });
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this text?")) return;
        try {
            await axiosInstance.delete(`/animation-texts/${id}`);
            fetchTexts();
        } catch (err) {
            console.error(err);
        }
    };

    const handleEdit = (item) => {
        setFormData({ text: item.text, sub_text: item.sub_text || '' });
        setEditId(item.id);
    };

    const handleCancel = () => {
        setEditId(null);
        setFormData({ text: '', sub_text: '' });
    };

    return (
        <div className="max-w-4xl mx-auto p-6 text-gray-200">
            <div className="flex items-center gap-4 mb-8">
                <div className="p-3 bg-cyan-500/20 rounded-lg text-cyan-400">
                    <Globe size={32} />
                </div>
                <div>
                    <h1 className="text-3xl font-bold">3D Globe Texts</h1>
                    <p className="text-gray-400 text-sm">Manage floating words and sub-labels.</p>
                </div>
            </div>

            {/* Form */}
            <div className="bg-gray-800/50 border border-gray-700 p-6 rounded-2xl mb-10">
                <form onSubmit={handleSubmit} className="flex flex-col md:flex-row gap-4 items-end">
                    
                    {/* Main Text Input */}
                    <div className="relative flex-grow w-full">
                        <label className="block text-xs text-gray-400 mb-1 ml-1">Main Text</label>
                        <Type className="absolute left-3 top-9 text-gray-500" size={18} />
                        <input 
                            type="text" 
                            name="text"
                            value={formData.text}
                            onChange={handleChange}
                            placeholder="e.g. REACT" 
                            className="w-full bg-gray-900 border border-gray-600 text-white pl-10 p-3 rounded-lg focus:border-cyan-500 focus:outline-none uppercase font-bold"
                        />
                    </div>

                    {/* Sub Text Input */}
                    <div className="relative flex-grow w-full">
                        <label className="block text-xs text-gray-400 mb-1 ml-1">Sub Text</label>
                        <FileText className="absolute left-3 top-9 text-gray-500" size={18} />
                        <input 
                            type="text" 
                            name="sub_text"
                            value={formData.sub_text}
                            onChange={handleChange}
                            placeholder="e.g. UI LIBRARY" 
                            className="w-full bg-gray-900 border border-gray-600 text-white pl-10 p-3 rounded-lg focus:border-cyan-500 focus:outline-none font-mono text-sm"
                        />
                    </div>

                    <div className="flex gap-2 w-full md:w-auto">
                        <button type="submit" className="bg-cyan-600 hover:bg-cyan-500 text-white px-6 py-3 rounded-lg font-bold flex items-center justify-center gap-2 flex-grow">
                            {editId ? <Save size={18}/> : <Plus size={18}/>}
                            {editId ? 'Save' : 'Add'}
                        </button>
                        {editId && (
                            <button type="button" onClick={handleCancel} className="bg-gray-700 text-white px-4 py-3 rounded-lg">
                                <X size={18} />
                            </button>
                        )}
                    </div>
                </form>
                {status.msg && <p className={`mt-2 text-sm ${status.type === 'error' ? 'text-red-400' : 'text-green-400'}`}>{status.msg}</p>}
            </div>

            {/* List */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {texts.map((item) => (
                    <div key={item.id} className="bg-gray-900 border border-gray-700 p-4 rounded-xl flex justify-between items-center group hover:border-cyan-500/50 transition-colors">
                        <div>
                            <div className="font-sans text-cyan-300 font-bold text-xl tracking-wide">{item.text}</div>
                            <div className="font-mono text-gray-500 text-xs mt-1 border-l-2 border-gray-700 pl-2">
                                // {item.sub_text || 'NULL'}
                            </div>
                        </div>
                        <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button onClick={() => handleEdit(item)} className="text-yellow-400 hover:bg-gray-800 p-2 rounded"><Edit2 size={16}/></button>
                            <button onClick={() => handleDelete(item.id)} className="text-red-400 hover:bg-gray-800 p-2 rounded"><Trash2 size={16}/></button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default AnimationTextManage;